import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BRAND, STATUS_TONE } from './auditTones';
import { countExportedFindings } from './downloadAuditCsv';

/**
 * What the audit found, above the detail: the headline counts, the strict-mode
 * switch, the colour legend, and - importantly - the differences that are
 * structural rather than data errors.
 *
 * That last part is not decoration. Several columns differ on EVERY row for
 * reasons no data entry can close (route is an "N/A" placeholder, the two
 * systems mean different things by "start date", six columns have no field in
 * our schema at all). Without saying so up front the screen reads as though the
 * facility's records are in a far worse state than they are.
 *
 * The tiles are ordered by what the reviewer acts on: the two counts that mean
 * work come first and carry colour, the reference figures are muted.
 */

// Falcon's palette, as tints. Kept literal because these tiles need a soft fill
// and a solid icon from the same hue, which no single utility class gives.
const VARIANT = {
  primary: { color: BRAND.base, soft: BRAND.soft },
  success: { color: '#00864e', soft: 'rgba(0,210,122,0.14)' },
  warning: { color: '#b3671a', soft: 'rgba(245,128,62,0.14)' },
  danger: { color: '#c9243c', soft: 'rgba(230,55,87,0.12)' },
  info: { color: '#1a86b8', soft: 'rgba(39,188,253,0.14)' },
  neutral: { color: '#5e6e82', soft: 'rgba(120,132,156,0.12)' }
};

const Tile = ({ icon, label, value, variant = 'neutral', hint, emphasis }) => {
  const tone = VARIANT[variant] || VARIANT.neutral;
  return (
    <Col xs={6} md={4} xxl={2}>
      <div
        className="rounded-3 p-3 h-100 d-flex align-items-start"
        style={{
          backgroundColor: emphasis ? tone.soft : 'transparent',
          border: `1px solid ${emphasis ? 'transparent' : 'rgba(120,132,156,0.2)'}`
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-2"
          style={{
            width: 34,
            height: 34,
            backgroundColor: emphasis ? 'rgba(255,255,255,0.65)' : tone.soft,
            color: tone.color
          }}
        >
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="min-w-0">
          <div className="fs-7 fw-bold lh-1" style={{ color: emphasis ? tone.color : undefined }}>
            {value}
          </div>
          <div className="fs-10 fw-medium text-800 mt-1">{label}</div>
          {hint && <div className="fs-11 text-600">{hint}</div>}
        </div>
      </div>
    </Col>
  );
};

Tile.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.string,
  hint: PropTypes.string,
  emphasis: PropTypes.bool
};

const AuditSummary = ({ result, strict, onStrictChange, onDownloadFindings, onDownloadComparison, busy }) => {
  const [showGaps, setShowGaps] = useState(false);
  const s = result.summary;
  const needsWork = s.FIELD_MISMATCH + s.MISSING_IN_MYM + s.UNKNOWN_PERSON;

  return (
    <>
      {/* Headline: what the reviewer has to do something about. */}
      <Row className="g-2 mb-2">
        <Tile
          icon="exclamation-circle"
          label="Needs review"
          value={s.FIELD_MISMATCH}
          variant="danger"
          hint="values disagree"
          emphasis
        />
        <Tile
          icon="ban"
          label="No active prescription"
          value={s.MISSING_IN_MYM}
          variant="danger"
          hint="on the export, not active here"
          emphasis
        />
        <Tile
          icon="user"
          label="Unknown person"
          value={s.UNKNOWN_PERSON}
          variant={s.UNKNOWN_PERSON ? 'danger' : 'neutral'}
          hint="not on this facility"
          emphasis={!!s.UNKNOWN_PERSON}
        />
        <Tile
          icon="exchange-alt"
          label="Only in MapYourMeds"
          value={s.EXTRA_IN_MYM}
          variant="warning"
          hint="absent from the export"
          emphasis
        />
        <Tile
          icon="info-circle"
          label="Known gaps only"
          value={s.SYSTEMIC_ONLY}
          variant="warning"
          hint="structural, not errors"
        />
        <Tile
          icon="check-circle"
          label="Fully agree"
          value={s.EXACT_MATCH}
          variant="success"
        />
      </Row>

      {/* Reference figures - context, not work. */}
      <Row className="g-2 mb-3">
        <Tile icon="file-import" label="Rows in the export" value={s.exportRows} />
        <Tile icon="prescription" label="Active with us" value={s.mymActiveRows} />
        <Tile icon="link" label="Matched up" value={s.paired} variant="info" />
        <Tile icon="table" label="Fields compared" value={s.comparedFieldCount} />
        <Tile
          icon="filter"
          label="Columns skipped"
          value={s.unmappedColumnCount}
          variant={s.unmappedColumnCount ? 'warning' : 'neutral'}
        />
        <Col xs={6} md={4} xxl={2}>
          <div
            className="rounded-3 p-3 h-100 d-flex flex-column justify-content-center"
            style={{ border: '1px solid rgba(120,132,156,0.2)' }}
          >
            <Form.Check
              type="switch"
              id="audit-strict"
              className="mb-0"
              label={<span className="fw-medium fs-10">Strict mode</span>}
              checked={strict}
              disabled={busy}
              onChange={e => onStrictChange(e.target.checked)}
            />
            <div className="fs-11 text-600 mt-1">re-runs the audit</div>
          </div>
        </Col>
      </Row>

      {needsWork === 0 && (
        <Alert variant="success" className="py-2 d-flex align-items-center">
          <FontAwesomeIcon icon="check-circle" className="me-2" />
          <span className="fs-10">
            Nothing needs review — every row on the export matched a prescription and agreed on all
            comparable fields.
          </span>
        </Alert>
      )}

      {strict && (
        <Alert variant="warning" className="py-2 fs-10 d-flex">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2 mt-1 flex-shrink-0" />
          <div>
            <strong>Strict mode is on.</strong> A field counts as agreeing only where both systems
            recorded it the same way. Red here means <em>not recorded identically</em> — it does not
            mean the medication is wrong. Brand names no longer meet their ingredient, so more rows
            appear unmatched on both sides. Frequency, prescriber, strength and dose keep their
            normal comparison, because matching those as text marks every row red and tells you
            nothing.
          </div>
        </Alert>
      )}

      {result.systemicGaps && result.systemicGaps.length > 0 && (
        <Alert variant="info" className="py-2">
          <div className="d-flex justify-content-between align-items-start">
            <div className="fs-10 d-flex">
              <FontAwesomeIcon icon="info-circle" className="me-2 mt-1 flex-shrink-0" />
              <div>
                <strong>
                  {result.systemicGaps.length} columns in this file have no field in MapYourMeds.
                </strong>{' '}
                They differ on every row and no data entry closes them, so they are excluded from
                the row verdicts.
              </div>
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-3 text-decoration-none flex-shrink-0"
              onClick={() => setShowGaps(v => !v)}
            >
              {showGaps ? 'Hide' : 'Show'}
              <FontAwesomeIcon icon={showGaps ? 'chevron-up' : 'chevron-down'} className="ms-1" />
            </Button>
          </div>
          <Collapse in={showGaps}>
            <div>
              <ul className="mb-0 mt-2 fs-10">
                {result.systemicGaps.map(g => (
                  <li key={g.fieldId}>
                    <strong>{g.header || g.label}</strong> — {g.why}
                  </li>
                ))}
              </ul>
            </div>
          </Collapse>
        </Alert>
      )}

      {result.columns && result.columns.unmapped && result.columns.unmapped.length > 0 && (
        <Alert variant="warning" className="py-2 fs-10 d-flex align-items-start">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2 mt-1 flex-shrink-0" />
          <div>
            <strong>Not compared:</strong> {result.columns.unmapped.map(u => u.header).join(', ')}.
            Go back to the mapping if any of those should have been.
          </div>
        </Alert>
      )}

      <Card className="mb-3 shadow-none" style={{ border: '1px solid rgba(120,132,156,0.2)' }}>
        <Card.Body className="py-2 px-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex flex-wrap align-items-center gap-3 fs-10">
            <span className="text-600 fw-medium">Cell colours</span>
            {Object.entries(STATUS_TONE).map(([key, tone]) => (
              <span key={key} className="d-inline-flex align-items-center">
                <span
                  className="d-inline-block rounded-1 me-1"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: tone.bg,
                    border: `1px solid ${tone.color}`
                  }}
                />
                <span style={{ color: tone.color }}>{tone.label}</span>
              </span>
            ))}
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="primary" onClick={onDownloadComparison} disabled={busy}>
              <FontAwesomeIcon icon="download" className="me-1" />
              Comparison CSV
            </Button>
            <Button size="sm" variant="falcon-default" onClick={onDownloadFindings} disabled={busy}>
              <FontAwesomeIcon icon="download" className="me-1" />
              Findings CSV
              <Badge bg="secondary" className="ms-2">
                {countExportedFindings(result)}
              </Badge>
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

AuditSummary.propTypes = {
  result: PropTypes.object.isRequired,
  strict: PropTypes.bool,
  onStrictChange: PropTypes.func.isRequired,
  onDownloadFindings: PropTypes.func.isRequired,
  onDownloadComparison: PropTypes.func.isRequired,
  busy: PropTypes.bool
};

export default AuditSummary;
